import {
  AfterViewInit,
  ChangeDetectorRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from "@angular/core";
import {AbstractControl, FormArray, FormBuilder, FormGroup} from "@angular/forms";
import {IModel} from "../../../../shared/view-model/imodel.interface";
import {GenericFormGroupComponent} from "./generic-form-group/generic-form-group.component";
import * as _ from "lodash";
import {AbstractVisbosComponent} from "../../components/abstract-visbos.component";
import {SessionActions} from "../../../../shared/actions/session.actions";
import * as util from "util";
import {isUndefined} from "util";

export function getControlName(control: AbstractControl): string {
  let controlName = null;
  let parent = control.parent;

  if (parent instanceof FormGroup || parent instanceof FormArray) {
    for (let key in parent.controls) {
      if (control === parent.controls[key]) {
        controlName = key;
      }
    }
  }

  // we either found a name or simply return null
  return controlName;
}

export function mergeFormGroups(target: FormGroup, source: FormGroup, override: boolean = false) {
  for (let ctrlName in source.controls) {
    if (!target.contains(ctrlName)) {
      target.addControl(ctrlName, source.get(ctrlName));
    } else {
      if (override) {
        target.setControl(ctrlName, source.get(ctrlName));
      }
    }
  }
}

export function diffFormGroups(target: FormGroup, source: FormGroup) {
  for (let ctrlName in target.controls) {
    if (source.contains(ctrlName)) {
      target.removeControl(ctrlName);
    }
  }
}

export function intersectFormGroups(target: FormGroup, source: FormGroup) {
  for (let ctrlName in target.controls) {
    if (!source.contains(ctrlName)) {
      target.removeControl(ctrlName);
    }
  }
}

/**
 * Created by afuhr on 29.11.2016.
 */
export abstract class AbstractVisbosControl<T extends IModel, C extends AbstractControl> extends AbstractVisbosComponent implements OnInit, OnChanges, AfterViewInit {

  private static _cuid = require("cuid");

  private _control: C;

  private _item: T;

  @Input()
  set item(value: T) {
    this._item = value;
    this.itemChange.emit(value);
  }

  get item(): T {
    return this._item;
  }

  @Input()
  set control(value: C) {
    this._control = value;
    this.controlChange.emit(value);
  }

  get control(): C {
    return this._control;
  }

  @ViewChildren(forwardRef(() => GenericFormGroupComponent))
  genericFormGroupChildren: QueryList<GenericFormGroupComponent> = new QueryList<GenericFormGroupComponent>();

  @ViewChildren(forwardRef(() => AbstractVisbosControl))
  childForms: QueryList<AbstractVisbosControl<any, any>> = new QueryList<AbstractVisbosControl<any, any>>();

  @Input()
  touchMode: boolean = false;

  @Input()
  storedControls: string[] = [];

  @Input()
  disabled: (i?: T) => boolean = () => !this._item || (this._item && this._item.is_editable);

  @Input()
  storeable: boolean = false;

  @Input()
  autosync: boolean = true;

  @Input()
  formHorizontal: boolean = true;


  @Output()
  controlChange: EventEmitter<AbstractControl> = new EventEmitter();

  @Output()
  itemChange: EventEmitter<T> = new EventEmitter();

  ngOnChanges(changes: SimpleChanges): void {

    // Stored controls
    if (changes.storedControls) {
      if (this.genericFormGroupChildren) {
        this.genericFormGroupChildren.forEach(ch => {
          // Check if the control of each generic form group is in the stored controls array and if yes, enable the checkbox. Disable it otherwise
          if (this.storedControls.find(ctrl => ch.ctrl instanceof AbstractControl && ctrl === this.getControlPath(<AbstractControl>ch.ctrl))) {
            ch.storeControlEnabled = true;
          } else {
            ch.storeControlEnabled = false;
          }
        });
      }
    }

    if (changes.item) {
      this.onItemChanged();
      this.onItemChanges(changes.item.currentValue);
    }

    if (changes.control) {
      this.onControlChanged();
    }

  }

  onItemChanged() {
    if (this.autosync && this.control && this.control.pristine) {
      this.patchItemValues();
    } else {
      console.warn("Skipped item patching...", [this]);
    }
  }

  onControlChanged() {
    if (this.autosync && this.control && this.control.pristine) {
      this.patchDefaultValues();
      this.patchItemValues();
    } else {
      console.warn("Skipped control patching...", [this]);
    }
  }

  public patchItemValues() {
    if (!this.control) {
      console.error("Control not set, cannot patch item values!", [this]);
      return;
    }

    if (!this.item) {
      return;
    }

    let val = this.setDefaultValues(this.item);

    if (!isUndefined(val)) {
      this.control.patchValue(val);
    }

  }

  public patchDefaultValues() {
    if (!this.control) {
      console.error("Control not set, cannot patch default values!", [this]);
      return;
    }

    let val = this.setDefaultValues();

    if (!isUndefined(val)) {
      this.control.patchValue(val);
    }

  }

  public setDefaultValues(item?: T): { [controlKey: string]: any } | any {
    return undefined;
  }

  public setItemOrDefaultValues(item?: T, forceOverride: boolean = false) {

    console.warn("Deprecated function!", [this]);
    return;

  }


  constructor(protected fb: FormBuilder,
              cd: ChangeDetectorRef,
              sessionActions: SessionActions) {

    super(sessionActions, cd);

  }

  protected onItemChanges(item: T) {

  }

  focus() {

  }

  reset(fullReset: boolean = false) {

    // First, clear recursively all child elements
    this.control.reset();

    this.childForms.forEach(child => {
      child.reset();
    });

    this.genericFormGroupChildren.forEach(child => {
      child.reset();
    });

    // Then, set default values
    this.patchDefaultValues();
    this.patchItemValues();

  }

  clear() {
    this.item = undefined;
    this.reset();
  }

  ngOnInit(): void {
    super.ngOnInit();

    if (!this.control) {
      this.control = this.buildControlContent();
      this.onControlChanged();
      console.warn("No control provided, creating own control...", [this]);
    }

  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  protected setValue(item, path, defaultValue = null, addFunction?: () => void) {
    let value = _.get(item, path, defaultValue);
    if (util.isArray(value) && addFunction) {
      for (let v of value) {
        addFunction.call(this);
      }
    }
    return value;
  }

  protected isValueSet(item, path): boolean {
    return _.has(item, path);
  }

  public static cuid(): string {
    return AbstractVisbosControl._cuid();
  }

  public abstract buildControlContent(values?: T): C;

  getControlName(control: AbstractControl): string {
    return getControlName(control);
  }

  getControlPath(control: AbstractControl): string {
    let path: string[] = [];
    let ctrl = control;

    // Get name of all parents up to the root of the form
    while (ctrl) {
      let name = this.getControlName(ctrl);
      if (name) {
        path.push(name);
      }
      ctrl = ctrl.parent;
    }

    // Reverse the name array
    path = path.reverse();

    // Convert array into path string
    let pathString = path.join(".");

    return pathString;
  }

  storeControlValue(controls: AbstractControl | AbstractControl[], storeState: boolean) {
    if (util.isArray(controls)) {
      for (let ctrl of <AbstractControl[]>controls) {
        this.storeControl(ctrl, storeState);
      }
    } else {
      this.storeControl(<AbstractControl>controls, storeState);
    }
  }

  private storeControl(control: AbstractControl, storeState: boolean) {
    let pathKey = this.getControlPath(control);
    if (storeState) {
      this.storedControls.push(pathKey);
    } else {
      let i = this.storedControls.indexOf(pathKey);
      this.storedControls.splice(i, 1);
    }
  }

  public mergeFormGroups(target: FormGroup, source: FormGroup) {
    mergeFormGroups(target, source);
  }

}



// WEBPACK FOOTER //
// ./src/app/core/visbos-core/ui/forms/abstract-visbos-control.ts